import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Timer, Inbox, Radio, FileCheck2 } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const PAINS = [
  {
    title: "La question attend dans une boîte courriel",
    body: "Le chantier pose la question à 7h12. Elle attend que la bonne personne la remarque — souvent après le dîner, parfois après deux relances. Pendant ce temps, l'équipe sur place attend ou devine.",
  },
  {
    title: "La réponse se perd entre le chantier et le bureau",
    body: "Transférée, répondue, puis introuvable trois semaines plus tard quand quelqu'un demande « qui a approuvé ça? ». La réponse existait. Elle vivait juste dans le mauvais endroit.",
  },
  {
    title: "La même question revient",
    body: "Sans dossier central, le même RFI repart d'un autre chantier, d'un autre téléphone. Chaque réponse est réinventée au lieu d'être réutilisée.",
  },
];

const FLOW = [
  {
    n: "01",
    title: "Soumis une fois",
    body: "Le RFI entre par un seul canal — du chantier ou du bureau — et reçoit un numéro, un responsable et une date. Fini les fils de courriels.",
  },
  {
    n: "02",
    title: "Visible par la bonne personne, tout de suite",
    body: "La question arrive directement à celle ou celui qui peut y répondre, avec le plan et le contexte attachés. Personne ne transporte l'information à la main.",
  },
  {
    n: "03",
    title: "Répondue dans le même dossier",
    body: "La réponse vit avec la question, visible par tous ceux qui en ont besoin — aujourd'hui, et dans trois semaines quand quelqu'un demande qui a approuvé quoi.",
  },
];

const FAQ = [
  {
    q: "Est-ce que ça remplace notre logiciel de gestion de chantier ?",
    a: "Non. On connecte ce que vous utilisez déjà — Zoho Projects, Microsoft 365, Google, Autodesk — pour que le RFI ait un seul foyer. Votre équipe garde ses habitudes; l'information arrête de se perdre entre elles.",
  },
  {
    q: "C'est quoi, « en quelques heures » ?",
    a: "Chez Construction Horizon, le délai de réponse aux RFI a chuté de 43 % en six semaines — mesuré dans leurs propres dossiers. Les décisions se prennent maintenant en heures, pas en jours. Votre chiffre dépend de votre point de départ; on le mesure ensemble.",
  },
  {
    q: "Comment démarrer sans déranger les chantiers en cours ?",
    a: "Par une revue gratuite de 15 minutes, puis un seul processus à la fois — pratiqué d'abord sur une copie sécuritaire de vos données. Aucun changement en production avant que l'équipe soit prête.",
  },
];

export default function RfiPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Réponse aux RFI en quelques heures, pas en jours | Ocean Data — Montréal";
    document.documentElement.lang = "fr";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "fr-CA";
    el.href = `${SITE}/fr/reponse-rfi-quelques-heures`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/fr/reponse-rfi-quelques-heures`,
        name: "Réponse aux RFI en quelques heures, pas en jours",
        description:
          "Pour les entreprises de construction du Québec : des RFI soumis une fois, visibles par la bonne personne immédiatement, répondus dans le même dossier. 43 % plus rapide chez Construction Horizon — mesuré.",
        inLanguage: "fr-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Connexion du processus RFI — construction",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Montréal" },
          { "@type": "AdministrativeArea", name: "Québec" },
        ],
        audience: { "@type": "BusinessAudience", name: "Entreprises de construction" },
        description:
          "Nous connectons les outils que vous utilisez déjà pour que chaque demande d'information (RFI) soit soumise une fois, visible immédiatement par la bonne personne et répondue dans le même dossier.",
      },
      {
        "@type": "FAQPage",
        inLanguage: "fr-CA",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="fr">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Montréal · Construction
          </p>
          <h1
            data-testid="rfi-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="rfi-headline-line-1">Réponse aux RFI en quelques heures,</MaskedLine>
            <MaskedLine delay={0.3} testId="rfi-headline-line-2">
              <span className="text-brand-teal">pas en jours.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Le RFI décide si le chantier avance ou attend. On connecte les
            outils que vous avez déjà pour que chaque question soit soumise une
            fois, vue par la bonne personne, répondue dans le même dossier.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="rfi-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Montrez-moi où ça bloque
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revue gratuite de 15 minutes · Sans engagement</p>
          </motion.div>
        </div>
      </section>

      {/* Le vrai coût d'un RFI lent */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Le vrai coût d'un RFI lent
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Ce n'est pas la question qui coûte. C'est l'attente.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {PAINS.map((p, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`rfi-pain-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-4">
                    <Inbox size={16} />
                  </span>
                  <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug mb-3">{p.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* La version connectée */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            La version connectée
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Un seul foyer pour chaque question.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {FLOW.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div data-testid={`rfi-step-${i}`} className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-4">{s.n}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Preuve Horizon */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Mesuré, pas promis
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["43 %", "de délai de réponse aux RFI en moins chez Construction Horizon"],
              ["6 semaines", "du premier appel au résultat mesuré"],
              ["1 processus", "connecté — sans changer les outils de l'équipe"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`rfi-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div className="flex items-start gap-4 max-w-2xl">
                <Radio size={20} className="text-brand-gold shrink-0 mt-1" />
                <div>
                  <p className="font-display font-medium text-lg leading-snug text-white mb-2">
                    « En six semaines, le délai de réponse de notre équipe aux RFI a chuté de 43 %. Les décisions se prennent maintenant en heures, pas en jours. »
                  </p>
                  <p className="text-xs text-slate-400">Directeur des opérations, Construction Horizon</p>
                </div>
              </div>
              <Link
                to="/fr/journal/horizon-construction-etude-de-cas"
                data-testid="rfi-case-study-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                <FileCheck2 size={15} /> Lire l'étude de cas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Ce que les entrepreneurs nous demandent
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`rfi-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Votre prochain RFI pourrait être répondu avant le dîner.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Quinze minutes pour cartographier comment vos RFI voyagent
                  aujourd'hui — et voir exactement où les heures se perdent.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="rfi-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  <Timer size={18} />
                  Demander ma revue gratuite
                </button>
                <p className="text-xs text-slate-400">
                  Tous vos outils restent :{" "}
                  <Link to="/fr/gardez-vos-outils" className="text-brand-gold hover:text-white transition-colors">
                    on les connecte, on ne remplace rien
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
